import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";
import { Footer } from "../components/Footer";
import { useApp } from "../App";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";

export function SignupPage() {
  const GoogleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  const {
    login,
    register,
    loginWithGoogle,
    continueAsGuest,
    restaurant,
    isLoggedIn,
  } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect") || "/";

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isFormValid =
    name.trim() !== "" &&
    isValidEmail(email) &&
    password.trim() !== "" &&
    confirmPassword.trim() !== "" &&
    password === confirmPassword &&
    isTermsAccepted;

  useEffect(() => {
    if (isLoggedIn) {
      navigate(
        redirectTo === "checkout"
          ? "/checkout"
          : redirectTo === "profile"
          ? "/profile"
          : "/"
      );
    }
  }, [isLoggedIn, navigate, redirectTo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      alert("Passwords do not match");
      return;
    }
    try {
      setIsLoading(true);
      await register(email, password, name);
      setIsLoading(false);
    } catch (err) {
      console.error(err);
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async (credentialResponse: any) => {
    setIsLoading(true);

    if (!credentialResponse.credential) {
      alert("Google login failed");
      setIsLoading(false);
      return;
    }

    try {
      const dto = { idToken: credentialResponse.credential };
      await loginWithGoogle(dto);
    } catch (error) {
      alert("Login error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGuestSignUp = async () => {
    setIsLoading(true);
    await continueAsGuest();
    setIsLoading(false);
  };
  return (
    <>
      <div className="min-h-screen relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/1640772/pexels-photo-1640772.jpeg?auto=compress&cs=tinysrgb&w=1600"
            alt="Thai Food Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>

        <div className="relative z-10 min-h-screen bg-gradient-to-br from-emerald-900/20 via-transparent to-thai-900/20 flex items-center justify-center py-8 sm:py-12 mobile-padding">
          <div className="max-w-md w-full space-y-6 sm:space-y-8 animate-fade-in-up">
            {/* Back Button */}
            <div className="flex items-center animate-slide-in-left">
              <Link to="/">
                <Button
                  variant="ghost"
                  className="flex items-center space-x-2 text-white hover:text-emerald-200 transition-all duration-300 hover:-translate-x-1 bg-white/10 backdrop-blur-sm"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Back to Menu</span>
                  <span className="sm:hidden">Back</span>
                </Button>
              </Link>
            </div>

            <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur-sm animate-zoom-in">
              <CardHeader className="text-center pb-4 sm:pb-6">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-full flex items-center justify-center mx-auto mb-4 animate-rotate-in">
                  <span className="text-white font-bold text-lg sm:text-2xl font-display">
                    E
                  </span>
                </div>
                <CardTitle className="text-2xl sm:text-3xl font-bold text-gray-900 font-display animate-slide-in-down">
                  Join {restaurant?.name}
                </CardTitle>
                <p
                  className="text-gray-600 mt-2 text-sm sm:text-base animate-slide-in-down"
                  style={{ animationDelay: "0.1s" }}
                >
                  Create your account to start ordering
                </p>
              </CardHeader>

              <CardContent className="space-y-4 sm:space-y-6 animate-slide-in-up">
                {/* Google Signup Button */}
                <GoogleOAuthProvider clientId={GoogleClientId}>
                  <GoogleLogin
                    onSuccess={handleGoogleSignUp}
                    onError={() => console.log("Login Failed")}
                  />
                </GoogleOAuthProvider>
                {/* Guest Login Button */}
                <Button
                  onClick={handleGuestSignUp}
                  variant="outline"
                  className="w-full h-12 sm:h-14 border-orange-500 text-orange-600 font-medium hover:bg-orange-50 transition-all duration-300 hover:scale-105 flex items-center justify-center"
                  disabled={isLoading}
                >
                  <svg
                    className="w-5 h-5 sm:w-6 sm:h-6 mr-3 text-orange-600"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5.121 17.804A9 9 0 1118.88 6.195M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span className="text-sm sm:text-base">
                    Continue as Guest
                  </span>
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <Separator className="w-full" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-gray-500">
                      Or continue with email
                    </span>
                  </div>
                </div>

                <form
                  onSubmit={handleSubmit}
                  className="space-y-4 sm:space-y-6"
                >
                  <div className="space-y-2">
                    <Label
                      htmlFor="name"
                      className="text-sm font-medium text-gray-700"
                    >
                      Full Name
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                      <Input
                        id="name"
                        type="text"
                        placeholder="Enter your full name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="pl-10 sm:pl-12 h-12 sm:h-14 border-gray-200 focus:border-emerald-500 focus:ring-emerald-500 text-sm sm:text-base transition-all duration-300"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="email"
                      className="text-sm font-medium text-gray-700"
                    >
                      Email Address
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 sm:pl-12 h-12 sm:h-14 border-gray-200 focus:border-emerald-500 focus:ring-emerald-500 text-sm sm:text-base transition-all duration-300"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="password"
                      className="text-sm font-medium text-gray-700"
                    >
                      Password
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Create a password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 sm:pl-12 pr-10 sm:pr-12 h-12 sm:h-14 border-gray-200 focus:border-emerald-500 focus:ring-emerald-500 text-sm sm:text-base transition-all duration-300"
                        required
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 sm:h-10 w-8 sm:w-10 p-0"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" />
                        ) : (
                          <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="confirm-password"
                      className="text-sm font-medium text-gray-700"
                    >
                      Confirm Password
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                      <Input
                        id="confirm-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Confirm your password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pl-10 sm:pl-12 h-12 sm:h-14 border-gray-200 focus:border-emerald-500 focus:ring-emerald-500 text-sm sm:text-base transition-all duration-300"
                        required
                      />
                    </div>
                  </div>

                  <div className="flex items-start">
                    <input
                      id="terms"
                      name="terms"
                      type="checkbox"
                      checked={isTermsAccepted}
                      onChange={(e) => setIsTermsAccepted(e.target.checked)}
                      className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded mt-0.5"
                      required
                    />
                    <label
                      htmlFor="terms"
                      className="ml-2 block text-xs sm:text-sm text-gray-700"
                    >
                      I agree to the{" "}
                      <a
                        href="#"
                        className="text-emerald-600 hover:text-emerald-700"
                      >
                        Terms of Service
                      </a>{" "}
                      and{" "}
                      <a
                        href="#"
                        className="text-emerald-600 hover:text-emerald-700"
                      >
                        Privacy Policy
                      </a>
                    </label>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 sm:h-14 btn-primary text-base sm:text-lg font-semibold"
                    disabled={!isFormValid || isLoading} // Button is disabled if terms not accepted or loading
                  >
                    {isLoading ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Creating Account...</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <span>Create Account</span>
                        <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                      </div>
                    )}
                  </Button>
                </form>

                <div className="mt-6 sm:mt-8 text-center">
                  <p className="text-gray-600 text-sm sm:text-base">
                    Already have an account?{" "}
                    <Link
                      to={`/login${
                        redirectTo !== "/" ? `?redirect=${redirectTo}` : ""
                      }`}
                      className="font-medium text-emerald-600 hover:text-emerald-700 transition-colors duration-300"
                    >
                      Sign in here
                    </Link>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
